import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import Modal from "../../../components/Modal";
import IncidentParameterForm from "./form/incident-parameter/IncidentParameterForm";
import { BigBoldText } from "../settings/setting.style";
import {
  MediumText,
  ReportCategoryCard,
  CategoryCardGrid,
  AnalyticsContainer,
} from "./style";
import { useAnalyticsWrapper, useViewStateWrapper } from "../../../store/store";
import { ANALYTICS_GRID_KEY_PREFIX } from "../../../utils/helpers/analyticsGridKey";
import FallAnalysis from "./fall-analysis";
import CaregiverAudit from "./caregiver-audit";

const Analytics = () => {
  const {
    setReportType,
    setGlobalAnaylyticsFacility,
    setGlobalAnalyticsSelectedIncidentType,
  } = useAnalyticsWrapper((state) => ({
    setReportType: state.setReportType,
    setGlobalAnaylyticsFacility: state.setGlobalAnaylyticsFacility,
    setGlobalAnalyticsSelectedIncidentType:
      state.setGlobalAnalyticsSelectedIncidentType,
  }));

  const navigate = useNavigate();

  // Back at the categories means a fresh start, so drop saved report grid views.
  useEffect(() => {
    const viewState = useViewStateWrapper.getState();
    viewState.clearGridViewsWithPrefix(ANALYTICS_GRID_KEY_PREFIX);
    viewState.setDrilldown(null);
  }, []);
  const [openFallAnalysisModal, setOpenFallAnalysisModal] = useState(false);
  const [openWoundAnalysisModal, setOpenWoundAnalysisModal] = useState(false);
  const [openCaregiverAuditModal, setOpenCaregiverAuditModal] = useState(false);
  const [activeReportModal, setActiveReportModal] = useState<
    | "incident-list"
    | "repeat-incident"
    | "incident-trend"
    | "status-report"
    | "wound-analysis"
    | "behavior-analysis"
    | "incident-rate"
    | "injury-report"
    | null
  >(null);

  const reportModalTitleMap: Record<
    Exclude<typeof activeReportModal, null>,
    string
  > = {
    "incident-list": "Incident List Parameters",
    "repeat-incident": "Repeat Incident Parameters",
    "incident-trend": "Incident Trend Parameters",
    "status-report": "Status Report Parameters",
    "wound-analysis": "Wound Analysis Parameters",
    "behavior-analysis": "Behavior Analysis Parameters",
    "incident-rate": "Incidents per 1,000 Beds Parameters",
    "injury-report": "Injury Report Parameters",
  };

  const openReportModal = (
    reportKey: Exclude<typeof activeReportModal, null>
  ) => {
    setReportType(reportKey);
    setActiveReportModal(reportKey);
  };

  const closeReportModal = () => {
    setReportType("");
    setActiveReportModal(null);
    // Don't reset global analytics here - values should persist while viewing the report
  };

  const handleReportNavigation = (
    reportKey: Exclude<typeof activeReportModal, null>
  ) => {
    document.body.style.overflow = "unset";

    switch (reportKey) {
      case "wound-analysis":
        navigate(`/user/analytics/wound-analysis`);
        break;
      case "behavior-analysis":
        navigate(`/user/analytics/behavior-analysis`);
        break;
      default:
        navigate(`/user/analytics/report?report=${reportKey}`);
    }
  };

  return (
    <AnalyticsContainer>
      <BigBoldText>Analytics</BigBoldText>
      <div className="mb-6 mt-10">
        <MediumText>Report Categories</MediumText>
      </div>
      <CategoryCardGrid>
        <ReportCategoryCard
          onClick={() => openReportModal("incident-list")}
          data-testid="incidentList-categ-card"
        >
          <div className="font-extrabold md:text-xl xl:text-2xl">
            Incident List
          </div>
          <div className="mt-2 text-sm">
            View Incident list for one or more facilities, one or more incidents
            within a specified duration.
          </div>
        </ReportCategoryCard>
        <ReportCategoryCard
          data-testid="repeatIncidentList-categ-card"
          onClick={() => openReportModal("repeat-incident")}
        >
          <div className="font-extrabold md:text-xl xl:text-2xl">
            Repeat Incident
          </div>
          <div className="mt-2 text-sm">
            View repeat incidents for one or more facilities and by incident
            types within a specified duration.
          </div>
        </ReportCategoryCard>
        <ReportCategoryCard
          data-testid="incidentTrend-categ-card"
          onClick={() => openReportModal("incident-trend")}
        >
          <div className="font-extrabold md:text-xl xl:text-2xl">
            {" "}
            Trend Report
          </div>
          <div className="mt-2 text-sm">
            View a trend report for a facility within a specified duration.
          </div>
        </ReportCategoryCard>
        <ReportCategoryCard
          data-testid="fallAnalysis-categ-card"
          onClick={() => setOpenFallAnalysisModal(true)}
        >
          <div className="font-extrabold md:text-xl xl:text-2xl">
            {" "}
            Fall Analysis
          </div>
          <div className="mt-2 text-sm">
            View a comprehensive custom report data for a facility within a
            specified duration.
          </div>
        </ReportCategoryCard>
        <ReportCategoryCard
          data-testid="statusAnalysis-categ-card"
          onClick={() => openReportModal("status-report")}
        >
          <div className="font-extrabold md:text-xl xl:text-2xl">
            {" "}
            Status Report
          </div>
          <div className="mt-2 text-sm">
            View a comprehensive custom report data for a status
          </div>
        </ReportCategoryCard>
        <ReportCategoryCard
          data-testid="woundAnalysis-categ-card"
          onClick={() => openReportModal("wound-analysis")}
        >
          <div className="font-extrabold md:text-xl xl:text-2xl">
            {" "}
            Wound Analysis
          </div>
          <div className="mt-2 text-sm">
            View wound analysis for one or more facilities by skin issue types
            within a specified duration.
          </div>
        </ReportCategoryCard>
        {/* TODO: Enable Behavior Analysis report once backend is ready and Charts are complete */}
        <ReportCategoryCard
          data-testid="behaviorAnalysis-categ-card"
          onClick={() => openReportModal("behavior-analysis")}
        >
          <div className="font-extrabold md:text-xl xl:text-2xl">
            {" "}
            Behavior Analysis
          </div>
          <div className="mt-2 text-sm">
            View behavior analysis for one or more facilities by behavior types
            within a specified duration.
          </div>
        </ReportCategoryCard>
        {/* Incident Rate report hidden — not actively worked on; re-enable when ready */}
        {/* <ReportCategoryCard
          data-testid="repeatIncidentList-categ-card"
          onClick={() => openReportModal("incident-rate")}
        >
          <div className="font-extrabold md:text-xl xl:text-2xl">
            Incident Rate
          </div>
          <div className="mt-2 text-sm">
            Track and compare incident frequency across facilities by
            calculating incidents per 1,000 beds.
          </div>
        </ReportCategoryCard> */}
        <ReportCategoryCard
          data-testid="injuryReport-categ-card"
          onClick={() => openReportModal("injury-report")}
        >
          <div className="font-extrabold md:text-xl xl:text-2xl">
            Injury Report
          </div>
          <div className="mt-2 text-sm">
            View incidents that resulted in injuries.
          </div>
        </ReportCategoryCard>
        <ReportCategoryCard
          data-testid="caregiverAudit-categ-card"
          onClick={() => setOpenCaregiverAuditModal(true)}
        >
          <div className="font-extrabold md:text-xl xl:text-2xl">
            Caregiver Audit
          </div>
          <div className="mt-2 text-sm">
            Find a caregiver's incidents across all roles — reporter, nurse, CNA,
            and last provided care.
          </div>
        </ReportCategoryCard>
        {/* <ReportCategoryCard
          data-testid="fallAnalysis-categ-card"
          onClick={() => navigate("/user/analytics/builder")}
        >
          <div className="font-extrabold md:text-xl xl:text-2xl">
            {" "}
            Report Builder
          </div>
          <div className="mt-2 text-sm">
            View a comprehensive fall report analytics for one or more
            facilities.
          </div>
        </ReportCategoryCard> */}
      </CategoryCardGrid>

      <Modal open={openFallAnalysisModal} className="w-full">
        <FallAnalysis
          handleCloseModal={() => {
            setOpenFallAnalysisModal(false);
            // Don't reset global analytics here - values should persist while viewing the report
          }}
          title="Fall Analysis"
          handleFormSubmit={() => {
            document.body.style.overflow = "unset";
            navigate("/user/analytics/fall-analysis");
          }}
        />
      </Modal>

      <Modal open={openCaregiverAuditModal} className="w-full">
        <CaregiverAudit
          handleCloseModal={() => setOpenCaregiverAuditModal(false)}
          title="Caregiver Audit"
          handleFormSubmit={() => {
            document.body.style.overflow = "unset";
            navigate("/user/analytics/caregiver-audit");
          }}
        />
      </Modal>

      <Modal open={Boolean(activeReportModal)} close={closeReportModal}>
        {activeReportModal && (
          <IncidentParameterForm
            title={reportModalTitleMap[activeReportModal]}
            handleCloseModal={closeReportModal}
            handleSubmit={() => handleReportNavigation(activeReportModal)}
          />
        )}
      </Modal>
    </AnalyticsContainer>
  );
};

export default Analytics;
